import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import roleService from '../../services/roleService';
import {
  Box,
  Typography,
  Card,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Breadcrumbs,
  Switch,
  FormControlLabel,
  Tooltip,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';

// Permission categories and their fields for the dialog
const PERMISSION_CATEGORIES = [
  {
    label: 'Content Visibility',
    permissions: [
      { field: 'can_view_unpublished', label: 'View unpublished content' },
    ],
  },
  {
    label: 'Content Management',
    permissions: [
      { field: 'can_manage_assignments', label: 'Manage assignments' },
      { field: 'can_manage_modules', label: 'Manage modules' },
      { field: 'can_manage_pages', label: 'Manage pages' },
      { field: 'can_manage_files', label: 'Manage files' },
      { field: 'can_manage_announcements', label: 'Manage announcements' },
      { field: 'can_manage_discussion_topics', label: 'Manage discussion topics' },
      { field: 'can_manage_question_banks', label: 'Manage question banks' },
      { field: 'can_manage_rubrics', label: 'Manage rubrics' },
      { field: 'can_manage_release_conditions', label: 'Manage release conditions' },
    ],
  },
  {
    label: 'Grading',
    permissions: [
      { field: 'can_grade', label: 'Grade submissions' },
      { field: 'can_view_gradebook', label: 'View gradebook' },
      { field: 'can_export_grades', label: 'Export grades' },
      { field: 'can_manage_grade_settings', label: 'Manage grade settings' },
    ],
  },
  {
    label: 'Communication',
    permissions: [
      { field: 'can_post_in_discussions', label: 'Post in discussions' },
      { field: 'can_moderate_discussions', label: 'Moderate discussions' },
    ],
  },
  {
    label: 'Course Administration',
    permissions: [
      { field: 'can_manage_roster', label: 'Manage roster' },
      { field: 'can_view_analytics', label: 'View analytics' },
      { field: 'can_use_student_view', label: 'Use student view' },
      { field: 'can_use_ai', label: 'Use AI features' },
    ],
  },
  {
    label: 'Role Classification',
    permissions: [
      { field: 'can_submit_assignments', label: 'Submit assignments' },
      { field: 'can_view_own_grades', label: 'View own grades' },
      { field: 'can_view_linked_student_data', label: 'View linked student data' },
      { field: 'receives_grades', label: 'Receives grades' },
    ],
  },
];

// All permission field names for initializing empty state
const ALL_PERMISSION_FIELDS = PERMISSION_CATEGORIES.flatMap((cat) =>
  cat.permissions.map((p) => p.field)
);

const getEmptyPermissions = () => {
  const perms = {};
  ALL_PERMISSION_FIELDS.forEach((field) => {
    perms[field] = false;
  });
  return perms;
};

const getInitialFormState = () => ({
  name: '',
  slug: '',
  description: '',
  ...getEmptyPermissions(),
});

/**
 * Converts a role name to a URL-friendly slug.
 * Lowercases, replaces spaces and non-alphanumeric chars with hyphens,
 * trims leading/trailing hyphens, and collapses consecutive hyphens.
 */
const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit'
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [formData, setFormData] = useState(getInitialFormState());
  const [saving, setSaving] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRoles = useCallback(async () => {
    try {
      const data = await roleService.getRoles();
      setRoles(data.results || data);
    } catch (err) {
      setError('Failed to load roles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // --- Dialog handlers ---

  const openCreateDialog = () => {
    setDialogMode('create');
    setEditingRoleId(null);
    setFormData(getInitialFormState());
    setSlugManuallyEdited(false);
    setDialogOpen(true);
  };

  const openEditDialog = (role) => {
    setDialogMode('edit');
    setEditingRoleId(role.id);
    const data = { name: role.name, slug: role.slug, description: role.description || '' };
    ALL_PERMISSION_FIELDS.forEach((field) => {
      data[field] = !!role[field];
    });
    setFormData(data);
    setSlugManuallyEdited(true); // Don't auto-generate slug for existing roles
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingRoleId(null);
    setFormData(getInitialFormState());
    setSlugManuallyEdited(false);
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData((prev) => ({
      ...prev,
      name,
      ...(dialogMode === 'create' && !slugManuallyEdited ? { slug: slugify(name) } : {}),
    }));
  };

  const handleSlugChange = (e) => {
    setSlugManuallyEdited(true);
    setFormData((prev) => ({ ...prev, slug: e.target.value }));
  };

  const handlePermissionToggle = (field) => {
    setFormData((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (dialogMode === 'create') {
        await roleService.createRole(formData);
        setSuccess('Role created successfully.');
      } else {
        await roleService.updateRole(editingRoleId, formData);
        setSuccess('Role updated successfully.');
      }
      closeDialog();
      fetchRoles();
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.slug?.[0] ||
        err.response?.data?.name?.[0] ||
        (dialogMode === 'create' ? 'Failed to create role.' : 'Failed to update role.');
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  // --- Clone handler ---

  const handleClone = async (role) => {
    setError('');
    try {
      await roleService.cloneRole(role.id);
      setSuccess(`Role "${role.name}" cloned successfully.`);
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to clone role.');
    }
  };

  // --- Delete handlers ---

  const openDeleteDialog = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;
    setDeleting(true);
    setError('');
    try {
      await roleService.deleteRole(roleToDelete.id);
      setSuccess(`Role "${roleToDelete.name}" deleted successfully.`);
      closeDeleteDialog();
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete role.');
      closeDeleteDialog();
    } finally {
      setDeleting(false);
    }
  };

  // --- Filtering ---

  const filteredRoles = roles.filter((role) =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- Render ---

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography color="text.secondary">Loading roles...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        px: 4,
        py: 3,
      }}>
        <Breadcrumbs aria-label="Breadcrumb navigation" sx={{ mb: 1.5 }}>
          <Typography
            component={Link}
            to="/admin"
            sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.85rem', '&:hover': { textDecoration: 'underline' } }}
          >
            Admin
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>Roles</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="h1" fontWeight={700}>
            Role Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add Role
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert
          severity="error"
          sx={{ mx: 4, mt: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setSuccess('')}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {success}
        </Alert>
      </Snackbar>

      {/* Content */}
      <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
        {/* Toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: 200, maxWidth: 400 }}
            inputProps={{ 'aria-label': 'Search roles' }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredRoles.length} role(s)
          </Typography>
        </Box>

        {/* Role Table */}
        {filteredRoles.length === 0 ? (
          <Box sx={{
            bgcolor: 'background.default',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 3,
            p: 6,
            textAlign: 'center',
          }}>
            <SecurityIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>No roles found</Typography>
            <Typography color="text.secondary">
              Create a new role to get started.
            </Typography>
          </Box>
        ) : (
          <Card variant="outlined">
            <Table aria-label="Course roles list">
              <TableHead>
                <TableRow>
                  {['Name', 'Slug', 'System', 'Members', 'Actions'].map((header) => (
                    <TableCell
                      key={header}
                      component="th"
                      scope="col"
                      sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        letterSpacing: '0.05em',
                        color: 'text.secondary',
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoles.map((role) => {
                  const hasMembers = role.member_count > 0;
                  const canDelete = !role.is_system && !hasMembers;

                  return (
                    <TableRow key={role.id} hover>
                      {/* Name */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {role.name}
                        </Typography>
                        {role.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {role.description}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Slug */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontFamily="'JetBrains Mono', monospace"
                          fontWeight={500}
                          color="primary.main"
                        >
                          {role.slug}
                        </Typography>
                      </TableCell>

                      {/* System badge */}
                      <TableCell>
                        {role.is_system ? (
                          <Chip
                            label="System"
                            size="small"
                            color="info"
                            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            Custom
                          </Typography>
                        )}
                      </TableCell>

                      {/* Member count */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontFamily="'JetBrains Mono', monospace"
                          fontWeight={500}
                        >
                          {role.member_count}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit role">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(role)}
                              aria-label={`Edit ${role.name}`}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Clone role">
                            <IconButton
                              size="small"
                              onClick={() => handleClone(role)}
                              aria-label={`Clone ${role.name}`}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {role.is_system ? (
                            <Tooltip title="System roles cannot be deleted">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled
                                  aria-label={`Cannot delete ${role.name} (system role)`}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : hasMembers ? (
                            <Tooltip title="Cannot delete a role with active members">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled
                                  aria-label={`Cannot delete ${role.name} (has active members)`}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Delete role">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => openDeleteDialog(role)}
                                aria-label={`Delete ${role.name}`}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}
      </Box>

      {/* Create / Edit Role Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="role-dialog-title"
      >
        <DialogTitle
          id="role-dialog-title"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          {dialogMode === 'create' ? 'Create New Role' : 'Edit Role'}
          <IconButton onClick={closeDialog} size="small" aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSave}>
          <DialogContent dividers sx={{ maxHeight: '65vh' }}>
            {/* Name */}
            <TextField
              label="Name"
              value={formData.name}
              onChange={handleNameChange}
              required
              fullWidth
              sx={{ mb: 2.5 }}
              inputProps={{ 'aria-required': 'true' }}
            />

            {/* Slug */}
            <TextField
              label="Slug"
              value={formData.slug}
              onChange={handleSlugChange}
              required
              fullWidth
              sx={{ mb: 2.5 }}
              helperText={
                dialogMode === 'create' && !slugManuallyEdited
                  ? 'Auto-generated from name. Edit to customize.'
                  : 'URL-friendly identifier for this role.'
              }
              inputProps={{
                'aria-required': 'true',
                'aria-describedby': 'slug-helper-text',
              }}
              FormHelperTextProps={{ id: 'slug-helper-text' }}
            />

            {/* Description */}
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              sx={{ mb: 3 }}
            />

            {/* Permission Toggles */}
            <Typography variant="h6" component="h3" fontWeight={600} sx={{ mb: 2 }}>
              Permissions
            </Typography>

            {PERMISSION_CATEGORIES.map((category) => (
              <Box key={category.label} sx={{ mb: 3 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={600}
                  letterSpacing="0.05em"
                  sx={{ display: 'block', mb: 1 }}
                >
                  {category.label}
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    px: 2,
                    py: 0.5,
                  }}
                >
                  {category.permissions.map((perm) => (
                    <FormControlLabel
                      key={perm.field}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mx: 0,
                        borderBottom: 1,
                        borderColor: 'divider',
                        py: 0.5,
                        '&:last-of-type': { borderBottom: 0 },
                      }}
                      labelPlacement="start"
                      label={
                        <Typography variant="body2">{perm.label}</Typography>
                      }
                      control={
                        <Switch
                          checked={!!formData[perm.field]}
                          onChange={() => handlePermissionToggle(perm.field)}
                          inputProps={{ 'aria-label': perm.label }}
                        />
                      }
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeDialog} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving
                ? (dialogMode === 'create' ? 'Creating...' : 'Saving...')
                : (dialogMode === 'create' ? 'Create Role' : 'Save Changes')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role{' '}
            <Typography component="span" fontWeight={700}>
              {roleToDelete?.name}
            </Typography>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDeleteDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
